// @flow

import { get, intersection, isEqual, sampleSize } from 'lodash';

import { useLocalStorage } from '@zapier/common-hooks';
import { useQuery } from '@apollo/react-hooks';
import { isBrowser, isCrawler } from '@zapier/common-utils';

import banditRecommendationQuery from './banditRecommendationQuery';

type RecommendationDetails = {
  count?: number,
  name: string,
  options: Array<string>,
};

type Options = {
  shouldSkip?: boolean,
};

// Generates a random recommendation from the given options.
// This method is called as a backup if the BE service fails to process the request.
const generateRandomRecommendation = (props: RecommendationDetails) => ({
  name: props.name,
  recommendation: sampleSize(props.options, props.count || 1),
  id: null,
});

/**
 * Hook to fetch a recommendation from Bandit Microservice.
 * If the request to microservice fails for any reason,
 * useBandit generates its own recommendation and saves it to
 * localStorage for future use.
 * Usage:
 * const [isLoading, result] = useBandit({
 *    name: "name-of-optimization",
 *    options: ["variant1", "variant2"]
 * });
 */
const useBandit = (
  recommendationDetails: RecommendationDetails,
  opts: Options = { shouldSkip: false }
) => {
  const { count = 1, name, options } = recommendationDetails;
  const [
    recommendationFromLocalStorage,
    saveRecommendationToLocalStorage,
  ] = useLocalStorage(`bandit:${name}`);

  // If there is a previous recommendation, we skip firing the request.
  const previousRecommendation = get(
    recommendationFromLocalStorage,
    ['recommendation'],
    []
  );
  // If the hook generated a random recommendation because of a BE failture,
  // then this ensures that the locally stored recommendation is still valid
  // so that we don't fetch a new recommendation.
  // Existing recommendation is only valid if its length matches count and
  // it is a subset of the provided options
  const hasValidLocalRecommendation =
    get(previousRecommendation, ['length']) === count &&
    intersection(previousRecommendation, options).length ===
      previousRecommendation.length;
  // If Holliday was down then the recommendation was randomly generated
  // in the microservice or locally, and doesn't have an `id`.
  const hasRealLocalRecommendation = !!get(recommendationFromLocalStorage, [
    'id',
  ]);
  // Indicates that the recommendation is real and not random and
  // that it's still a valid recommentation based on possible `options`.
  const hasRealAndValidLocalRecommendation =
    hasValidLocalRecommendation && hasRealLocalRecommendation;
  const userAgent =
    typeof window === 'undefined'
      ? null
      : window.navigator && window.navigator.userAgent;
  // Bandit recommendations may be run server-side, but should never be run
  // for crawlers to avoid SEO impacts.
  const shouldSkip =
    opts.shouldSkip ||
    !isBrowser() ||
    isCrawler(userAgent) ||
    hasRealAndValidLocalRecommendation;
  const { loading, error, ...response } = useQuery(banditRecommendationQuery, {
    skip: shouldSkip,
    ssr: false,
    variables: {
      name,
      options,
      count,
    },
  });

  const result = hasRealAndValidLocalRecommendation
    ? recommendationFromLocalStorage
    : !loading && error
    ? generateRandomRecommendation(recommendationDetails)
    : get(response, ['data', 'banditRecommendation']);

  // Successful API results are also saved to localStorage so that
  // if the API fails later, the user will continue to have the
  // same experience from the successful result
  if (result && !isEqual(result, recommendationFromLocalStorage)) {
    saveRecommendationToLocalStorage(result);
  }

  return {
    isLoading: loading || false,
    result,
  };
};

export default useBandit;
